import { ClockCountdownIcon } from '@phosphor-icons/react';
import type { ComponentProps, ReactNode } from 'react';

import { Checkmark, XCircle } from '@remarkable/ark-icons';
import { Tag } from '@remarkable/ark-web';

import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import { connectSubscriptionHelpers } from 'src/utils/connectSubscriptionHelpers';
import { formatDate } from 'src/utils/formatDate';

type StatusTagProps = Partial<ComponentProps<typeof Tag>> & {
  subscription: CloudConnectSubscription | null;
  fallback?: ReactNode;
};
export const StatusTagEnterprise = ({
  subscription,
  fallback,
  ...props
}: StatusTagProps) => {
  if (subscription?.cancelAtPeriodEnd) {
    const cancelsAt = formatDate(subscription.cancelAt, {
      fallback: 'period end',
    });
    return (
      <Tag variant="themed-filled" className="!bg-warning-light" {...props}>
        <ClockCountdownIcon /> Cancels at {cancelsAt}
      </Tag>
    );
  }

  if (subscription?.cancelRequested) {
    return (
      <Tag variant="themed-filled" className="!bg-warning-light" {...props}>
        <ClockCountdownIcon /> Cancel requested
      </Tag>
    );
  }

  if (!subscription?.status) {
    return fallback || null;
  }

  if (connectSubscriptionHelpers.isOverdue(subscription)) {
    // This also handles the status 'past_due'
    return (
      <Tag variant="themed-filled" className="!bg-error-light" {...props}>
        <ClockCountdownIcon /> Overdue
      </Tag>
    );
  }

  switch (subscription.status) {
    case 'active': {
      return (
        <Tag variant="success-light" {...props}>
          <Checkmark /> Active
        </Tag>
      );
    }
    case 'trialing':
      return (
        <Tag variant="success-light" {...props}>
          <Checkmark /> Trial
        </Tag>
      );

    case 'canceled':
      return (
        <Tag variant="themed-filled" className="!bg-error-light" {...props}>
          <XCircle /> Canceled
        </Tag>
      );
    default:
      return null;
  }
};
